import React, { useState, useEffect } from "react";
import { Swords, Coins, Check, Loader2, Clock, LogIn, Copy, Share2 } from "lucide-react";

const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

const PlayerSlot = ({ name, ready, you, waiting, testid }) => (
  <div
    data-testid={testid}
    className={`rounded-xl border p-4 text-center transition-colors ${
      ready ? "border-emerald-500/50 bg-emerald-500/10" : "border-white/10 bg-white/5"
    }`}
  >
    <div className="font-display font-bold truncate text-white">
      {name || "Waiting…"}
      {you ? " (you)" : ""}
    </div>
    <div
      className={`text-xs mt-1 flex items-center justify-center gap-1 ${
        ready ? "text-emerald-400" : "text-muted-38"
      }`}
    >
      {ready ? (
        <>
          <Check className="w-3 h-3" /> Ready
        </>
      ) : waiting ? (
        "Not joined"
      ) : (
        "Not ready"
      )}
    </div>
  </div>
);

const Lobby = ({ match, user, onJoin, onReady, onCancel, joining, readying, inviteLink }) => {
  const amCreator = match.creator.email === user.email;
  const opp = match.opponent;
  const amOpponent = opp && opp.email === user.email;
  const inLobby = amCreator || amOpponent;
  const myPart = amCreator ? match.creator : amOpponent ? opp : null;
  const otherName = amCreator ? opp?.username || (match.open ? "your mate" : match.target) : match.creator.username;

  const [copied, setCopied] = useState(false);
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
    } catch (e) {
      /* clipboard blocked — the link is still shown for manual copy */
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const shareLink = async () => {
    const text = match.stake ? `I've staked £${match.stake} (virtual) that my XI beats yours on Footie Clash. Tap to play:` : "Free friendly: bet my XI beats yours on Footie Clash. Tap to play:";
    if (navigator.share) {
      try {
        await navigator.share({ title: "Footie Clash wager", text, url: inviteLink });
        return;
      } catch (e) {
        if (e?.name === "AbortError") return;
      }
    }
    copyLink();
  };
  const openLink = match.open && amCreator && !opp;

  const [remaining, setRemaining] = useState(0);
  useEffect(() => {
    const upd = () => setRemaining(Math.max(0, Math.round((match.expires_at || 0) - Date.now() / 1000)));
    upd();
    const t = setInterval(upd, 1000);
    return () => clearInterval(t);
  }, [match.expires_at]);

  return (
    <div className="relative z-10 max-w-md mx-auto px-5 py-8" data-testid="lobby-screen">
      <div className="card-38 p-6 hover:translate-y-0 text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#10B981]/15 border border-[#10B981]/30 mb-3">
          <Swords className="w-7 h-7 text-[#10B981]" />
        </div>
        <h1 className="font-display font-extrabold text-2xl">Live match lobby</h1>
        <div className="flex items-center justify-center gap-1.5 mt-2 text-[#10B981]">
          <Coins className="w-4 h-4" />
          {match.stake ? (
            <>
              <span className="font-display font-bold">£{match.stake}</span>
              <span className="text-muted-38 text-sm ml-1">stake · winner takes £{match.pot}</span>
            </>
          ) : (
            <span className="font-display font-bold">Friendly · nothing on the line</span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3 mt-6">
          <PlayerSlot testid="lobby-creator-slot" name={match.creator.username} ready={match.creator.ready} you={amCreator} />
          <PlayerSlot testid="lobby-opponent-slot" name={opp?.username || match.invited_name || match.target} ready={!!opp?.ready} you={amOpponent} waiting={!opp} />
        </div>

        <div className="flex items-center justify-center gap-1.5 text-muted-38 text-xs mt-5">
          <Clock className="w-3.5 h-3.5" /> {match.open ? `Link open for ${remaining >= 3600 ? `${Math.floor(remaining / 3600)}h` : fmt(remaining)}` : `Auto-cancels in ${fmt(remaining)} if nobody joins`}
        </div>

        {openLink && (
          <div className="mt-5" data-testid="invite-link-box">
            <button
              data-testid="invite-share-btn"
              onClick={shareLink}
              className="btn-green wager-glow w-full rounded-xl py-4 font-extrabold text-lg flex items-center justify-center gap-2"
            >
              <Share2 className="w-5 h-5" /> Send link to your mate
            </button>
            <button
              data-testid="invite-copy-btn"
              onClick={copyLink}
              className="mt-2 w-full rounded-xl border border-white/10 bg-black/25 px-3 py-2.5 flex items-center gap-2 text-left hover:border-[#38BDF8]/40 transition-colors"
            >
              <span data-testid="invite-link-input" className="flex-1 min-w-0 truncate text-xs text-slate-300">{inviteLink.replace(/^https?:\/\//, "")}</span>
              <span className="text-xs font-semibold text-[#38BDF8] flex items-center gap-1 shrink-0">
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? "Copied" : "Copy"}
              </span>
            </button>
            {match.invited_name && (
              <p data-testid="rematch-sent-note" className="text-[#38BDF8] text-xs mt-2">{match.challenge ? `Challenge sent to ${match.invited_name}. They can accept from Home or their alerts.` : `Rematch sent to ${match.invited_name}. They can accept straight from their results screen.`}</p>
            )}
            <p className="text-muted-38 text-[11px] mt-2">
              When they tap it, you both go straight into the draft. No sign-up, no ready button.
            </p>
            <div className="mt-4 flex items-center justify-center gap-2 text-muted-38 text-sm" data-testid="waiting-for-mate">
              <Loader2 className="w-4 h-4 animate-spin" /> Waiting for your mate to open it…
            </div>
          </div>
        )}

        {openLink ? null : !inLobby ? (
          <button
            data-testid="lobby-join-btn"
            onClick={onJoin}
            disabled={joining}
            className="btn-green w-full rounded-xl py-3.5 font-semibold mt-5 flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <LogIn className="w-4 h-4" /> {joining ? "Joining…" : `Accept & join (£${match.stake})`}
          </button>
        ) : myPart?.ready ? (
          <div className="mt-5 flex items-center justify-center gap-2 text-muted-38 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Waiting for {otherName} to ready up…
          </div>
        ) : (
          <button
            data-testid="lobby-ready-btn"
            onClick={onReady}
            disabled={readying}
            className="btn-green w-full rounded-xl py-3.5 font-semibold mt-5 disabled:opacity-50"
          >
            {readying ? "…" : "I'm ready — start drafting"}
          </button>
        )}

        {amCreator && (
          <button
            data-testid="lobby-cancel-btn"
            onClick={onCancel}
            className="btn-ghost w-full rounded-xl py-3 font-medium text-slate-300 mt-3"
          >
            Cancel &amp; refund
          </button>
        )}
      </div>
    </div>
  );
};

export default Lobby;
